/* DuFrane booking: timezone pin + mobile continuity. Ported from clients/trafford/shopify-theme/assets/trafford-booking.js, selectors rescoped. */
/*
 * Trafford booking calendar: timezone pinning + mobile continuity.
 *
 * Two separate jobs, both behaviour-only. Nothing here re-parents Appointo's
 * DOM: if the app ships a markup change these enhancements stop firing and the
 * widget still works exactly as the vendor intended.
 */
(function () {
  var MOBILE_MAX = 999;

  /* ====================================================================== *
   * 1. TIMEZONE
   *
   * The showroom is a physical room in Austin. Neil, Aug 24: "what's the use
   * case where I would want to select a different time zone for a place that
   * I'm going to have to physically be in?" So we hid the app's timezone
   * picker and printed "All times Central (US)" above the slots.
   *
   * That was wrong, and Neil caught it from Sydney on Sep 1: the app renders
   * slots in the BROWSER's timezone, and hiding the picker hid that fact
   * rather than changing it. He saw 1:00am-6:00am slots (10am-3pm Central,
   * converted to AEST) sitting under a label asserting Central.
   *
   * Fix: actually pin the widget to Central by driving the app's own select,
   * which is what its slot rendering reads from. Measured on the live widget
   * before writing this: setting it to Australia/Sydney turned 10:00 AM into
   * 1:00 AM, and setting it back to America/Chicago restored 10:00 AM.
   *
   * The label is now DERIVED, never asserted. If the pin ever fails (the app
   * renames the select, drops the option, or this script does not run) the
   * page states whatever timezone is genuinely in effect, or says nothing at
   * all. A missing label is fine. A false one is what caused this bug.
   * ====================================================================== */

  var STORE_TZ = 'America/Chicago';
  var STORE_TZ_LABEL = 'All times Central';
  var PIN_THROTTLE_MS = 1200; // never fight the app in a tight loop
  var lastPin = 0;

  function widgetRoot() {
    return document.querySelector('.df-sr-book__cal');
  }

  function tzSelect() {
    var root = widgetRoot();
    return root && root.querySelector('#appointo-timezone-select, .appointo-timezone-select');
  }

  /* "AEST", "GMT+10", "CDT" — for the fallback label only. */
  function friendlyZone(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      }).formatToParts(new Date());
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'timeZoneName') return parts[i].value;
      }
    } catch (e) {}
    return null;
  }

  function setLabel(text) {
    var root = widgetRoot();
    if (!root) return;
    // CSS `content` needs a quoted string; JSON.stringify gives us one safely.
    root.style.setProperty('--df-tz-label', text ? JSON.stringify(text) : '""');
  }

  function pinTimezone() {
    var sel = tzSelect();

    // No select at all: say nothing rather than guess.
    if (!sel) {
      setLabel('');
      return;
    }

    if (sel.value === STORE_TZ) {
      setLabel(STORE_TZ_LABEL);
      return;
    }

    var offered = false;
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === STORE_TZ) { offered = true; break; }
    }

    // Central is not on offer: leave the app alone and tell the truth about
    // whatever it IS showing, so the customer can still convert correctly.
    if (!offered) {
      var zone = friendlyZone(sel.value);
      setLabel(zone ? 'All times ' + zone : '');
      return;
    }

    var now = Date.now();
    if (now - lastPin < PIN_THROTTLE_MS) return;
    lastPin = now;

    sel.value = STORE_TZ;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    setLabel(STORE_TZ_LABEL);
  }

  /* The select does NOT exist at page load. It is created inside the SLOTS pane
     (.appointo-calendar-day-events), so it only appears once the customer picks
     a date. An initial poll therefore finds nothing and, if it gives up on a
     timer, never fires at all: that was the first attempt at this fix, and it
     failed exactly that way when the date was picked more than 20s after load.

     So we observe our own container, which IS server-rendered and present from
     the first paint, and pin whenever the select shows up. No timeout, no
     expiry. pinTimezone() is a cheap no-op once the value is already Central,
     so an observer this broad cannot thrash. */
  function watchTimezone(tries) {
    tries = tries || 0;
    var root = widgetRoot();

    if (!root) {
      if (tries < 60) window.setTimeout(function () { watchTimezone(tries + 1); }, 300);
      return;
    }

    try { pinTimezone(); } catch (e) {}

    if (window.MutationObserver) {
      /* Coalesced to one call per frame, and wrapped so nothing we do can ever
         throw inside Appointo's own render path. Measured before adding this:
         the observer fires roughly once per date pick, not hundreds of times,
         so this is insurance rather than a fix for a known problem. */
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          try { pinTimezone(); } catch (e) {}
        });
      }).observe(root, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchTimezone);
  } else {
    watchTimezone();
  }

  /* ====================================================================== *
   * 2. MOBILE CONTINUITY
   *
   * On mobile the widget stacks (calendar above, slots below), so tapping a
   * date changes content that is entirely off-screen. It reads as "nothing
   * happened". This brings the slots into view the moment a date is chosen.
   *
   * Two bugs this previously had, both worth not reintroducing:
   *   1. It measured the slots pane on a fixed 260ms timer. The slots come
   *      from a network call taking seconds, so it measured a pane that had
   *      not been populated or revealed yet. Now it POLLS.
   *   2. Its "already in view?" guard was `top > 60% of viewport || top < 0`.
   *      A display:none element reports a rect of all zeros, so top === 0
   *      satisfied neither branch and the scroll never fired. Visibility is
   *      now checked explicitly via offsetParent before position is trusted.
   * ====================================================================== */

  var POLL_MS = 120;
  var GIVE_UP_MS = 6000;

  function visible(el) {
    return !!el && el.offsetParent !== null;
  }

  function slotsReady() {
    var pane = document.querySelector('.df-sr-book__cal .appointo-right');
    if (!visible(pane)) return null;
    if (!pane.querySelector('.appointo-slot')) return null;
    return pane;
  }

  function revealWhenReady() {
    var waited = 0;
    var timer = window.setInterval(function () {
      waited += POLL_MS;
      var pane = slotsReady();

      if (!pane) {
        if (waited >= GIVE_UP_MS) window.clearInterval(timer);
        return;
      }

      window.clearInterval(timer);

      var top = pane.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        pane.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, POLL_MS);
  }

  document.addEventListener('click', function (e) {
    if (window.innerWidth > MOBILE_MAX) return;
    if (!e.target || !e.target.closest) return;

    var cell = e.target.closest('.df-sr-book__cal .vanilla-calendar-date');
    if (!cell || cell.className.indexOf('disabled') !== -1) return;

    revealWhenReady();
  });
})();

/* DuFrane: one showing per booking. The app's product setting allows multiple slot selection (same product
   as the live page), so until that is switched off in Appointo, picking a slot here clears any other. */
(function () {
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var slot = e.target.closest('.df-sr-book__cal .appointo-slot');
    if (!slot) return;
    window.setTimeout(function () {
      var all = document.querySelectorAll('.df-sr-book__cal .appointo-slot.appointo-slot-selected');
      for (var i = 0; i < all.length; i++) {
        if (all[i] !== slot && all[i].classList.contains('appointo-slot-selected')) all[i].click();
      }
    }, 0);
  }, true);
})();
